import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { devicesApi, type Device } from '../services/devicesApi';

export type AppMode = 'warehouse' | 'ocr';

type IdentityGate = {
  ok: boolean;
  reason?: string | null;
  detail?: string | null;
};

type ModeContextType = {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  ocr_mode_selectable: boolean;
  identity_gate: IdentityGate;
  active_device_id: string | null;
  loading: boolean;
};

const ModeContext = createContext<ModeContextType | undefined>(undefined);

const NAV_CTX_KEY = 'edgentik:lastNavContext:v1';
const STORAGE_LAST_SELECTED = 'ui.mode.last_selected';
const STORAGE_LAST_SELECTED_AT = 'ui.mode.last_selected_at';
function readNavDeviceId(): string | null {
  try {
    const raw = localStorage.getItem(NAV_CTX_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    const d = v?.device_id;
    return typeof d === 'string' && d.trim() ? d.trim() : null;
  } catch {
    return null;
  }
}

function clearNavDeviceId() {
  try {
    const raw = localStorage.getItem(NAV_CTX_KEY);
    if (!raw) return;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return;
    delete (v as any).device_id;
    localStorage.setItem(NAV_CTX_KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
}

function readDeviceIdFromQuery(): string | null {
  try {
    const sp = new URLSearchParams(window.location.search || '');
    const v = sp.get('device_id') || sp.get('device') || sp.get('deviceId');
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

function writeNavDeviceId(device_id: string) {
  try {
    const raw = localStorage.getItem(NAV_CTX_KEY);
    const v = raw ? JSON.parse(raw) : {};
    localStorage.setItem(NAV_CTX_KEY, JSON.stringify({ ...(v || {}), device_id }));
  } catch {
    // ignore
  }
}

function writeStoredMode(mode: AppMode) {
  try {
    localStorage.setItem(STORAGE_LAST_SELECTED, mode);
    localStorage.setItem(STORAGE_LAST_SELECTED_AT, String(Date.now()));
  } catch {
    // ignore
  }
}

export function useMode() {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error('useMode must be used within ModeProvider');
  return ctx;
}

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(readNavDeviceId());
  const [loading, setLoading] = useState(true);
  const [identityGate] = useState<IdentityGate>({ ok: true, reason: null, detail: null });
  /** Product shell is warehouse-only; app-level `/ocr/*` routes redirect back into warehouse. */
  const [mode] = useState<AppMode>('warehouse');

  // Clear any legacy "ocr" preference so embeds / bookmarks do not expect a hidden mode toggle.
  useEffect(() => {
    try {
      writeStoredMode('warehouse');
    } catch {
      // ignore
    }
  }, []);

  // Ensure we have a device context for demos: if none is selected, pick the first device.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const devices: Device[] = await devicesApi.listDevices();
        const requested = readDeviceIdFromQuery();
        const byQuery =
          requested && devices
            ? (devices || []).find((d) => typeof d.device_id === 'string' && d.device_id === requested) || null
            : null;
        const byActive =
          activeDeviceId && devices
            ? (devices || []).find((d) => typeof d.device_id === 'string' && d.device_id === activeDeviceId) || null
            : null;
        const first = (devices || []).find((d) => typeof d.device_id === 'string' && d.device_id) || null;
        if (!mounted) return;
        const chosen = (byQuery?.device_id || byActive?.device_id || first?.device_id || '').trim();
        if (chosen) {
          if (chosen !== activeDeviceId) {
            setActiveDeviceId(chosen);
            writeNavDeviceId(chosen);
          }
        } else if (activeDeviceId) {
          clearNavDeviceId();
          setActiveDeviceId(null);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeDeviceId]);

  useEffect(() => {
    setLoading(false);
  }, [activeDeviceId]);

  const ctxValue = useMemo<ModeContextType>(() => {
    const ocr_mode_selectable = false;
    return {
      mode,
      setMode: () => {
        /* no-op: warehouse-only shell */
      },
      ocr_mode_selectable,
      identity_gate: identityGate,
      active_device_id: activeDeviceId,
      loading,
    };
  }, [activeDeviceId, identityGate, loading, mode]);

  return <ModeContext.Provider value={ctxValue}>{children}</ModeContext.Provider>;
};
